import type { IEmployeeLookupRepository } from '../../infrastructure/repositories/CompositeEmployeeLookupRepository';

export type PinVerifyResult =
  | { ok: true;  employee: Awaited<ReturnType<IEmployeeLookupRepository['findByCodeOrDui']>> }
  | { ok: false; reason: 'employee-not-found' | 'pin-mismatch' };

/**
 * VerifyPinUseCase
 *
 * Reads the current employee from the local store (via IEmployeeLookupRepository)
 * at the moment of submission and verifies the entered PIN against it.
 *
 * This ensures PIN validation always uses the freshest local data — never a
 * stale React Query cache — which is essential for offline-first auth flows
 * (e.g. PIN re-enroll in the same offline session must be verifiable immediately).
 *
 * If the employee has no PIN enrolled (pinCode === null), the check is skipped
 * and the result is ok:true (no PIN restriction configured).
 */
export class VerifyPinUseCase {
  constructor(private readonly lookupRepo: IEmployeeLookupRepository) {}

  async execute(input: {
    accessRoleId: number;
    code:         string;
    pin:          string;
  }): Promise<PinVerifyResult> {
    const employee = await this.lookupRepo.findByCodeOrDui(input.accessRoleId, input.code);

    if (!employee) {
      return { ok: false, reason: 'employee-not-found' };
    }

    if (employee.pinCode !== null && input.pin !== employee.pinCode) {
      return { ok: false, reason: 'pin-mismatch' };
    }

    return { ok: true, employee };
  }
}
